/**
 * Row primitives for the Connections lists, in t3code's shape.
 *
 * The reference surface (t3code's "Authorized clients" / "Remote
 * environments") makes three choices this list did not, and every one of them
 * removes a box rather than adding one:
 *
 *  - **Status is a dot, not a pill.** A bordered badge per row put a second
 *    boxed thing beside the buttons and made the right-hand side read as
 *    cluttered. The dot costs 8px and carries the same scan signal.
 *  - **Metadata is one `·`-joined muted line under the name**, not a run of
 *    chips. Chips imply each fact is a separate object you might act on; these
 *    are one sentence about one row.
 *  - **Actions are text buttons**, and a row the user cannot fully resolve
 *    from here says why in plain prose beside them instead of going silent.
 *
 * Split out from `CloudExecutionTargetsSection.tsx` so both row renderers can
 * share it without pushing that file past the repo's file-size guardrail.
 */
import type { ReactNode } from "react";

import { SETTINGS_ROW_CONTROL_CLASS } from "./settingsControlDensity";

/** The box a non-row NOTICE (error, loading, empty state) sits in. */
export const ITEM_ROW_CLASSNAME = "rounded-xl px-3 py-3 sm:px-4";

/**
 * The box a real connection row sits in.
 *
 * `rounded-xl px-3 py-3 sm:px-4` is `SettingsRow`'s own box, verbatim — this
 * list sits on the same rail as every other settings surface, so it has no
 * business inventing its own density. The rounded hover plane is that
 * standard's depth mechanism for a whitespace-separated list —
 * `DESIGN.md` -> "The Settings Row Standard" — and this list was shipping the
 * `rounded-xl` corner with nothing to round. The notices deliberately do NOT
 * get it: a hover response on a static error string promises an interaction
 * that is not there.
 */
export const EXECUTION_TARGET_ROW_CLASSNAME =
  "rounded-xl px-3 py-3 transition-colors motion-reduce:transition-none hover:bg-accent sm:px-4";

/**
 * Content left, actions right — the settings standard's two tracks.
 *
 * Two earlier shapes failed here, both for one reason: the row's content did
 * not fill its width, so the layout had to invent somewhere to put the
 * emptiness. `[minmax(0,1fr)_7rem_16rem]` parked the status badge in a fixed
 * track of its own — measured on the 896px settings rail, the name "Forge"
 * ended at x=398 and its badge began at x=808, a 410px void between a label
 * and the state it described. Adding a kind column later only moved the
 * problem: a row with a primary button had a wider action track, so its state
 * column started further left than its neighbour's.
 *
 * `sm:grid-cols-[minmax(0,1fr)_minmax(10rem,auto)] sm:items-center sm:gap-8` is
 * `SettingsRow`'s grid, character for character. It cannot go ragged — the
 * control cell is the last track, so it sits flush against the row box on
 * every row however many controls it holds — and reusing it rather than
 * approximating it is the difference between this page agreeing with the rest
 * of Settings and merely resembling it.
 */
export const ITEM_ROW_INNER_CLASSNAME =
  "flex flex-col gap-3 sm:grid sm:grid-cols-[minmax(0,1fr)_minmax(10rem,auto)] sm:items-center sm:gap-8";

/**
 * The action cell.
 *
 * Reuses the settings standard's control cell verbatim so these hand-rolled
 * rows finally inherit its coarse-pointer density (`min-h-touch` below `md`) —
 * they never did, which is how a page of 28px icon buttons shipped to a
 * viewport driven by a finger.
 *
 * Unlike the icon clusters this replaces, the controls do NOT dim at rest.
 * Two text buttons are quiet enough already, and a hover-gated affordance does
 * not exist at all on a touch device.
 */
const EXECUTION_TARGET_ACTIONS_CLASSNAME = `${SETTINGS_ROW_CONTROL_CLASS} gap-4 max-md:flex-wrap`;

export type ExecutionTargetTone = "success" | "warning" | "destructive" | "muted";

const DOT_CLASSNAME: Record<ExecutionTargetTone, string> = {
  success: "bg-success",
  warning: "bg-warning motion-safe:animate-status-pulse",
  destructive: "bg-destructive",
  muted: "bg-muted-foreground/70",
};

/**
 * Status, as a dot.
 *
 * `aria-hidden`, and deliberately so: a dot cannot carry a status on its own —
 * "use icons or text in addition to colour" is the rule it would otherwise
 * break — so the status WORD always rides in the row's metadata line. The dot
 * is the fast visual scan; the word is the actual answer.
 */
function ExecutionStatusDot({ tone }: { readonly tone: ExecutionTargetTone }) {
  return (
    <span aria-hidden="true" className={`size-2 shrink-0 rounded-full ${DOT_CLASSNAME[tone]}`} />
  );
}

/**
 * One connection row's left half.
 *
 * `meta` is joined with `·` into a single muted line rather than rendered as
 * chips, and `note` is the one exceptional fact a row may carry ("Server
 * update available") on its own line beneath.
 */
export function ExecutionTargetIdentity({
  tone,
  name,
  statusLabel,
  accessibleStatus,
  meta,
  note,
}: {
  readonly tone: ExecutionTargetTone;
  /** Text, or the control that lets the name be edited in place. */
  readonly name: ReactNode;
  /** The status word. Never omitted — it is what the dot cannot say. */
  readonly statusLabel: string;
  readonly accessibleStatus: string;
  readonly meta: readonly string[];
  readonly note?: string | null;
}) {
  return (
    <div className="min-w-0 space-y-1">
      {/* `--sidebar-control-gap` is the house gap between a row's leading mark
          and its label; `min-h-5` matches `SettingsRow`'s title line so a row
          with a dot and one without still agree. */}
      <div className="flex min-h-5 min-w-0 items-center gap-[var(--sidebar-control-gap)]">
        <ExecutionStatusDot tone={tone} />
        {/* `text-sm font-medium tracking-[-0.005em]` is `SettingsRow`'s title.
            An unsized `font-medium` inherits `text-base`, which is a size this
            rail does not use for a row name. */}
        {typeof name === "string" ? (
          <p className="min-w-0 truncate text-sm font-medium tracking-[-0.005em] text-foreground">
            {name}
          </p>
        ) : (
          name
        )}
      </div>
      {/* And this is its description: 13px at 1.45, in `muted-foreground/80`. */}
      <p
        aria-label={accessibleStatus}
        className="truncate text-[13px] leading-[1.45] text-muted-foreground/80"
      >
        {[...meta, statusLabel].filter((part) => part !== "").join(" · ")}
      </p>
      {note === null || note === undefined ? null : (
        <p className="text-[13px] leading-[1.45] text-muted-foreground/80">{note}</p>
      )}
    </div>
  );
}

/**
 * The row's right half: why you cannot finish here, then how to act.
 *
 * The reference puts a plain sentence beside the button on rows the user
 * cannot fully resolve from this page ("Update the desktop app on that machine
 * to update this server"). It is hidden below `lg` rather than wrapped,
 * because on a narrow viewport it would push the control it explains down the
 * page.
 */
export function ExecutionTargetActions({
  hint,
  children,
}: {
  readonly hint?: string | null;
  readonly children?: ReactNode;
}) {
  return (
    <div
      // Names the control cell, so the settings standard's one load-bearing
      // geometry property — controls sharing a right edge across rows — can be
      // asserted against the real stylesheet.
      data-slot="settings-row-control"
      className={EXECUTION_TARGET_ACTIONS_CLASSNAME}
    >
      {hint === null || hint === undefined ? null : (
        <p className="hidden max-w-xs text-right text-[13px] leading-[1.45] text-muted-foreground/80 lg:block">
          {hint}
        </p>
      )}
      {children}
    </div>
  );
}
